import type { DateValue, RangeValue } from '@heroui/react';
import { EProductDuration, ESubSource } from '@quesmed/types/models';
import { ESubType } from '@quesmed/types/resolvers/query/admin';
import { create } from 'zustand';
import { EDateFilter, EProductCategory } from '@/config';
import type { OmitFunctions } from '@/types';
import { createSelectors } from './selector';

interface EarningComparisonState {
  // Period 1 filters
  duration1: EProductDuration;
  subType1: ESubType;
  source1: ESubSource;
  universityId1: number | null;
  searchText1: string;
  useNetEarning1: boolean;
  isUndergrad1: boolean;
  productCategory1: EProductCategory;
  dateFilter1: EDateFilter;
  range1: { from: Date | null | number; to: Date | null | number };
  dateRange1: RangeValue<DateValue> | undefined;
  range2: { from: Date | null | number; to: Date | null | number };
  dateRange2: RangeValue<DateValue> | undefined;
  // Period 2 filters
  duration2: EProductDuration;
  subType2: ESubType;
  source2: ESubSource;
  universityId2: number | null;
  searchText2: string;
  useNetEarning2: boolean;
  isUndergrad2: boolean;
  productCategory2: EProductCategory;
  dateFilter2: EDateFilter;

  isFilterDrawerOpen: boolean;
  isLoading: boolean;

  // Period 1 setters
  setDuration1: (duration: EProductDuration) => void;
  setSubType1: (subType: ESubType) => void;
  setSource1: (source: ESubSource) => void;
  setUniversityId1: (universityId: number | null) => void;
  setSearchText1: (searchText: string) => void;
  setUseNetEarning1: (useNetEarning: boolean) => void;
  setIsUndergrad1: (isUndergrad: boolean) => void;
  setProductCategory1: (productCategory: EProductCategory) => void;
  setDateFilter1: (dateFilter: EDateFilter) => void;
  setRange1: (range: EarningComparisonState['range1']) => void;
  setDateRange1: (dateRange: EarningComparisonState['dateRange1']) => void;
  // Period 2 setters
  setDuration2: (duration: EProductDuration) => void;
  setSubType2: (subType: ESubType) => void;
  setSource2: (source: ESubSource) => void;
  setUniversityId2: (universityId: number | null) => void;
  setSearchText2: (searchText: string) => void;
  setUseNetEarning2: (useNetEarning: boolean) => void;
  setIsUndergrad2: (isUndergrad: boolean) => void;
  setProductCategory2: (productCategory: EProductCategory) => void;
  setDateFilter2: (dateFilter: EDateFilter) => void;
  setRange2: (range: EarningComparisonState['range2']) => void;
  setDateRange2: (dateRange: EarningComparisonState['dateRange2']) => void;
  setIsFilterDrawerOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  reset: () => void;
}

const initialState: OmitFunctions<EarningComparisonState> = {
  duration1: EProductDuration.ALL,
  subType1: ESubType.ALL,
  source1: ESubSource.STRIPE,
  universityId1: null,
  searchText1: '',
  useNetEarning1: true,
  isUndergrad1: true,
  productCategory1: EProductCategory.ALL,
  dateFilter1: EDateFilter.THIS_MONTH,
  range1: { from: null, to: null },
  dateRange1: undefined,
  duration2: EProductDuration.ALL,
  subType2: ESubType.ALL,
  source2: ESubSource.STRIPE,
  universityId2: null,
  searchText2: '',
  useNetEarning2: true,
  isUndergrad2: true,
  productCategory2: EProductCategory.ALL,
  dateFilter2: EDateFilter.LAST_MONTH,
  range2: { from: null, to: null },
  dateRange2: undefined,
  isFilterDrawerOpen: false,
  isLoading: false,
};

export const earningComparisonState = create<EarningComparisonState>()((set) => ({
  ...initialState,
  setDuration1: (duration) => set({ duration1: duration }),
  setSubType1: (subType) => set({ subType1: subType }),
  setSource1: (source) => set({ source1: source }),
  setUniversityId1: (universityId) => set({ universityId1: universityId }),
  setSearchText1: (searchText) => set({ searchText1: searchText }),
  setUseNetEarning1: (useNetEarning) => set({ useNetEarning1: useNetEarning }),
  setIsUndergrad1: (isUndergrad) => set({ isUndergrad1: isUndergrad }),
  setProductCategory1: (productCategory) => set({ productCategory1: productCategory }),
  setDateFilter1: (dateFilter) => set({ dateFilter1: dateFilter }),
  setRange1: (range) => set({ range1: range }),
  setDateRange1: (dateRange) => set({ dateRange1: dateRange }),
  setDuration2: (duration) => set({ duration2: duration }),
  setSubType2: (subType) => set({ subType2: subType }),
  setSource2: (source) => set({ source2: source }),
  setUniversityId2: (universityId) => set({ universityId2: universityId }),
  setSearchText2: (searchText) => set({ searchText2: searchText }),
  setUseNetEarning2: (useNetEarning) => set({ useNetEarning2: useNetEarning }),
  setIsUndergrad2: (isUndergrad) => set({ isUndergrad2: isUndergrad }),
  setProductCategory2: (productCategory) => set({ productCategory2: productCategory }),
  setDateFilter2: (dateFilter) => set({ dateFilter2: dateFilter }),
  setRange2: (range) => set({ range2: range }),
  setDateRange2: (dateRange) => set({ dateRange2: dateRange }),
  setIsFilterDrawerOpen: (isOpen) => set({ isFilterDrawerOpen: isOpen }),
  setIsLoading: (isLoading) => set({ isLoading }),
  reset: () => set(initialState),
}));

export const useEarningComparisonState = () => earningComparisonState((state) => state);
export const useEarningComparison = createSelectors(earningComparisonState);
